
import axiosPrivateInstance, { apiService } from '../../api/axiosPrivateInstance';

/**
 * This file demonstrates how to use the axiosPrivateInstance in regular JS functions
 * without relying on React hooks.
 */

/**
 * Example function that makes a direct API call using the configured axios instance
 * @param {string} endpoint - The API endpoint to call
 * @param {object} data - The data to send in the request body
 * @returns {Promise} - The API response
 */
export const makeApiCall = async (endpoint, data = null) => {
  try {
    if (data) {
      const response = await axiosPrivateInstance.post(`api/${endpoint}`, data);
      return response.data;
    } else {
      const response = await axiosPrivateInstance.get(`api/${endpoint}`);
      return response.data;
    }
  } catch (error) {
    console.error(`API call to ${endpoint} failed:`, error);
    return { error: error.response?.data || error.message };
  }
};

/**
 * Example function that uses the apiService utility methods
 * @param {string} entity - The entity to fetch
 * @param {string} id - Optional ID for the entity
 * @returns {Promise} - The API response
 */
export const fetchEntityData = async (entity, id = null) => {
  try {
    if (id) {
      // For a specific entity with ID
      return await apiService.get(`${entity}/${id}`);
    } else {
      // For a collection of entities
      return await apiService.get(entity);
    }
  } catch (error) {
    console.error(`Failed to fetch ${entity}:`, error);
    return { error: error.message };
  }
};

/**
 * Example function that updates an entity
 * @param {string} entity - The entity to update
 * @param {object} data - The data to update
 * @param {string} id - The ID of the entity to update
 * @returns {Promise} - The API response
 */
export const updateEntityData = async (entity, data, id) => {
  try {
    return await apiService.put(entity, data, id);
  } catch (error) {
    console.error(`Failed to update ${entity}:`, error);
    return { error: error.message };
  }
};

/**
 * Example function that creates a new entity
 * @param {string} entity - The entity to create
 * @param {object} data - The data for the new entity
 * @returns {Promise} - The API response
 */
export const createEntityData = async (entity, data) => {
  try {
    return await apiService.post(entity, data);
  } catch (error) {
    console.error(`Failed to create ${entity}:`, error);
    return { error: error.message };
  }
};

/**
 * Example function that deletes an entity
 * @param {string} entity - The entity to delete
 * @param {string} id - The ID of the entity to delete
 * @returns {Promise} - The API response
 */
export const deleteEntityData = async (entity, id) => {
  try {
    return await apiService.delete(entity, id);
  } catch (error) {
    console.error(`Failed to delete ${entity}:`, error);
    return { error: error.message };
  }
};

/**
 * Example function that uploads a file
 * @param {string} entity - The entity to upload to
 * @param {File} file - The file to upload
 * @param {object} metadata - Additional metadata for the file
 * @returns {Promise} - The API response
 */
export const uploadFile = async (entity, file, metadata = {}) => {
  try {
    return await apiService.postForm(entity, file, metadata);
  } catch (error) {
    console.error(`Failed to upload file to ${entity}:`, error);
    return { error: error.message };
  }
};

/**
 * Example function that downloads a file
 * @param {string} entity - The entity to download from
 * @returns {Promise<Blob|File>} - The downloaded file
 */
export const downloadFile = async (entity) => {
  try {
    return await apiService.getStream(entity);
  } catch (error) {
    console.error(`Failed to download file from ${entity}:`, error);
    return { error: error.message };
  }
};

/**
 * Example function that generates a PDF
 * @param {string} entity - The entity to generate PDF for
 * @param {object} data - The data for PDF generation
 * @returns {Promise<Blob>} - The generated PDF
 */
export const generatePdf = async (entity, data) => {
  try {
    return await apiService.postStream(entity, data);
  } catch (error) {
    console.error(`Failed to generate PDF for ${entity}:`, error);
    return { error: error.message };
  }
};
